import {
  BUILD_TYPE_COPY,
  BUILD_TYPE_META,
  BuildTypeEnum,
  EngagementTypeEnum,
  UrgencyTierEnum,
  type BuildType,
  type EngagementType,
  type UrgencyTier,
} from '@flashpoint/site-dna';
import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../stepProps';
import { OptionCard } from '../OptionCard';

export function EngagementStep({ lead, queueField }: StepProps) {
  const e = lead.engagement;

  const serverDueSlice = e.urgency.dueAt
    ? new Date(e.urgency.dueAt).toISOString().slice(0, 16)
    : '';
  const [dueLocal, setDueLocal] = useState(serverDueSlice);
  const dueDirty = useRef(false);

  useEffect(() => {
    const slice = e.urgency.dueAt
      ? new Date(e.urgency.dueAt).toISOString().slice(0, 16)
      : '';
    setDueLocal(slice);
    dueDirty.current = false;
  }, [lead.id]);

  useEffect(() => {
    const slice = e.urgency.dueAt
      ? new Date(e.urgency.dueAt).toISOString().slice(0, 16)
      : '';
    if (!dueDirty.current) setDueLocal(slice);
  }, [e.urgency.dueAt]);

  useEffect(() => {
    const slice = e.urgency.dueAt
      ? new Date(e.urgency.dueAt).toISOString().slice(0, 16)
      : '';
    if (dueDirty.current && slice === dueLocal) dueDirty.current = false;
  }, [e.urgency.dueAt, dueLocal]);

  return (
    <div className="space-y-6">
      <section>
        <SectionLabel>Build type</SectionLabel>
        <p className="mb-3 text-xs text-nm-muted">
          Pick the build type that fits this engagement. Read the pitch on any card aloud during
          the discovery call.
        </p>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {BuildTypeEnum.options.map((opt) => {
            const meta = BUILD_TYPE_META[opt];
            const copy = BUILD_TYPE_COPY[opt];
            const selected = e.buildType === opt;
            return (
              <OptionCard
                key={opt}
                copy={copy}
                selected={selected}
                onSelect={() => {
                  queueField('engagement.buildType', opt);
                  if (meta.requiresMigration && !lead.migration) {
                    queueField('migration', { seoContinuity: 'Fresh Start' });
                  }
                }}
                badge={
                  <span style={{ color: meta.chipColor }}>
                    {meta.glyph}
                  </span>
                }
                ariaId={`build-type-${opt.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
              />
            );
          })}
        </div>
      </section>

      <section>
        <SectionLabel>Engagement type</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {EngagementTypeEnum.options.map((opt) => {
            const selected = e.engagementType === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => queueField('engagement.engagementType', opt as EngagementType)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                  selected
                    ? 'bg-flashpoint-spark text-nm-on-accent'
                    : 'border border-nm-border text-nm-ink hover:border-nm-accent'
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <SectionLabel>Urgency</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {UrgencyTierEnum.options.map((opt) => {
            const selected = e.urgency.tier === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => queueField('engagement.urgency.tier', opt as UrgencyTier)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                  selected
                    ? 'bg-flashpoint-ember text-nm-ink'
                    : 'border border-nm-border text-nm-ink hover:border-nm-accent'
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {e.urgency.tier !== 'Standard' && (
          <label className="mt-3 block">
            <span className="text-[11px] uppercase tracking-widest text-nm-muted">
              Hard deadline
            </span>
            <input
              type="datetime-local"
              value={dueLocal}
              onChange={(ev) => {
                const v = ev.target.value;
                dueDirty.current = true;
                setDueLocal(v);
                queueField('engagement.urgency.dueAt', v ? new Date(v).toISOString() : null);
              }}
              className="mt-1 w-full max-w-xs rounded-md border border-nm-border bg-nm-bg px-3 py-2 text-sm text-nm-ink outline-none focus:border-nm-accent"
            />
          </label>
        )}
      </section>

      {BUILD_TYPE_META[e.buildType as BuildType].requiresMigration && (
        <p className="rounded-md border border-nm-warn/30 bg-nm-warn-bg px-3 py-2 text-xs text-nm-warn">
          Build type requires the Migration step. We'll surface it next in the wizard.
        </p>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 text-[11px] uppercase tracking-widest text-nm-muted">{children}</div>;
}
